//* Bootstrap color-mode selector for the GoThassos site.
//*
//* The preference lives in a parent-domain "theme" cookie ("light" / "dark";
//* absent = follow the OS) so every subdomain (www, events) shares it,
//* and is applied as `data-bs-theme` on <html>.
//* The layout head applies it inline before first paint; this controller
//* handles user selection, follows OS changes while in auto mode, and
//* forces light mode while printing (schedules print on white paper).
import ApplicationController from "application_controller.js";

const COOKIE_KEY = "theme";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;
const MODES = ["light", "dark"];

export default class extends ApplicationController {
  debug = false;

  static targets = ["option", "label", "icon"];
  static classes = ["hidden"];

  connect() {
    this.log_function("connect");

    this.mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    this.systemChanged = this.systemChanged.bind(this);
    this.forceLight = this.forceLight.bind(this);
    this.restoreTheme = this.restoreTheme.bind(this);
    this.mediaQuery.addEventListener("change", this.systemChanged);
    window.addEventListener("beforeprint", this.forceLight);
    window.addEventListener("afterprint", this.restoreTheme);
    this.apply();
  }

  disconnect() {
    this.log_function("disconnect");

    this.mediaQuery.removeEventListener("change", this.systemChanged);
    window.removeEventListener("beforeprint", this.forceLight);
    window.removeEventListener("afterprint", this.restoreTheme);
  }

  select(event) {
    const mode = event.params.mode;
    if (MODES.includes(mode)) {
      this.writeCookie(mode, COOKIE_MAX_AGE);
    } else {
      this.writeCookie("", 0);
    }
    this.apply();
  }

  systemChanged() {
    if (!MODES.includes(this.storedMode)) this.apply();
  }

  apply() {
    document.documentElement.setAttribute("data-bs-theme", this.theme);
    this.syncOptions();
  }

  forceLight() {
    document.documentElement.setAttribute("data-bs-theme", "light");
  }

  restoreTheme() {
    document.documentElement.setAttribute("data-bs-theme", this.theme);
  }

  //* Mark the selected option active and mirror its text and icon in the button.
  syncOptions() {
    const selected = MODES.includes(this.storedMode) ? this.storedMode : "auto";

    this.optionTargets.forEach((option) => {
      const active = option.dataset.mode === selected;
      option.classList.toggle("active", active);
      option.setAttribute("aria-pressed", active);
      if (active && this.hasLabelTarget) this.labelTarget.textContent = option.textContent.trim();
    });

    this.iconTargets.forEach((icon) => {
      icon.classList.toggle(this.hiddenClass, icon.dataset.mode !== selected);
    });
  }

  writeCookie(value, maxAge) {
    document.cookie = `${COOKIE_KEY}=${value}; domain=.${this.cookieDomain}; path=/; max-age=${maxAge}; samesite=lax`;
  }

  //* The registrable domain (lvh.me in development, go-thassos.gr in production).
  get cookieDomain() {
    return location.hostname.split(".").slice(-2).join(".");
  }

  get storedMode() {
    return (document.cookie.match(/(?:^|;\s*)theme=(light|dark)(?:;|$)/) || [])[1] || null;
  }

  get theme() {
    if (MODES.includes(this.storedMode)) return this.storedMode;
    return this.mediaQuery.matches ? "dark" : "light";
  }
}
